import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Tab,
  Tabs
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShowChartIcon from '@mui/icons-material/ShowChart';
// Removed unused GroupAddIcon import
import ReceiptIcon from '@mui/icons-material/Receipt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';

// Mock data for admin dashboard
const dashboardStats = {
  totalUsers: 1248,
  activeUsers: 876,
  monthlyRevenue: '$18,450',
  revenueGrowth: '+12.3%',
  averageUserValue: '$24.56',
  ticketsOpen: 7,
  conversionRate: '8.2%',
  churnRate: '2.1%'
};

// Helper function to generate a color from a string (name)
function stringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

// Helper function to get initials from a name
function getInitials(name) {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2);
}

// Mock data for recent users
const recentUsers = [
  { id: 1, name: 'Emma Johnson', email: 'emma@example.com', date: '2 hours ago', plan: 'Pro', avatar: null },
  { id: 2, name: 'Michael Chen', email: 'michael@example.com', date: '4 hours ago', plan: 'Basic', avatar: null },
  { id: 3, name: 'Sophia Williams', email: 'sophia@example.com', date: '6 hours ago', plan: 'Enterprise', avatar: null },
  { id: 4, name: 'James Brown', email: 'james@example.com', date: '8 hours ago', plan: 'Pro', avatar: null },
];

// Mock data for recent transactions
const recentTransactions = [
  { id: 1, user: 'Emma Johnson', amount: '$79.00', date: '2 hours ago', status: 'Completed', plan: 'Pro (Monthly)' },
  { id: 2, name: 'Michael Chen', amount: '$29.00', date: '4 hours ago', status: 'Completed', plan: 'Basic (Monthly)' },
  { id: 3, name: 'Sophia Williams', amount: '$199.00', date: '6 hours ago', status: 'Completed', plan: 'Enterprise (Monthly)' },
  { id: 4, name: 'James Brown', amount: '$790.00', date: '1 day ago', status: 'Completed', plan: 'Pro (Annual)' },
];

// Mock data for recent tickets
const recentTickets = [
  { id: 1, user: 'Emma Johnson', subject: 'API Connection Issue', priority: 'High', status: 'Open', date: '2 hours ago' },
  { id: 2, user: 'Michael Chen', subject: 'Billing Question', priority: 'Medium', status: 'Open', date: '5 hours ago' },
  { id: 3, user: 'Sophia Williams', subject: 'Feature Request', priority: 'Low', status: 'Open', date: '1 day ago' },
  { id: 4, user: 'James Brown', subject: 'Account Access Problem', priority: 'High', status: 'In Progress', date: '1 day ago' },
];

// Mock data for plans
const planStats = [
  { name: 'Basic', users: 523, percentage: 42, revenue: '$15,167', color: '#2196f3' },
  { name: 'Pro', users: 612, percentage: 49, revenue: '$48,348', color: '#4caf50' },
  { name: 'Enterprise', users: 113, percentage: 9, revenue: '$22,487', color: '#f44336' },
];

function AdminDashboard() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h5" gutterBottom>Loading Dashboard...</Typography>
        <LinearProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }} gutterBottom>Admin Dashboard</Typography>
      
      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Users</Typography>
            </Box>
            <Typography variant="h4">{dashboardStats.totalUsers}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingUpIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color="success.main">
                +5.2% this month
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Monthly Revenue</Typography>
            </Box>
            <Typography variant="h4">{dashboardStats.monthlyRevenue}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingUpIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color="success.main">
                {dashboardStats.revenueGrowth} this month
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SupportAgentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Open Tickets</Typography>
            </Box>
            <Typography variant="h4">{dashboardStats.ticketsOpen}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/admin/tickets')}>
                View Tickets
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ShowChartIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Churn Rate</Typography>
            </Box>
            <Typography variant="h4">{dashboardStats.churnRate}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingDownIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color="success.main">
                -0.4% from last month
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Plan Distribution */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Plan Distribution</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', height: 25, mb: 2 }}>
              {planStats.map((plan) => (
                <Box 
                  key={plan.name}
                  sx={{ 
                    width: `${plan.percentage}%`, 
                    bgcolor: plan.color,
                    borderRadius: 0.5,
                    mr: 0.25
                  }} 
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {planStats.map((plan) => (
                <Box key={plan.name} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: plan.color,
                      mr: 1 
                    }} 
                  />
                  <Typography variant="body2">
                    {plan.name}: {plan.percentage}% ({plan.users} users)
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Revenue by Plan</Typography>
              {planStats.map((plan) => (
                <Box key={plan.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{plan.name}</Typography>
                  <Typography variant="body2" fontWeight="bold">{plan.revenue}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Monthly Revenue</Typography>
                <Typography variant="body2" fontWeight="bold">{dashboardStats.monthlyRevenue}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Recent Activity Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Recent Users" />
          <Tab label="Recent Transactions" />
          <Tab label="Support Tickets" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {/* Recent Users Tab */}
          {tabValue === 0 && (
            <>
              <List disablePadding>
                {recentUsers.map((user) => (
                  <ListItem 
                    key={user.id}
                    secondaryAction={
                      <IconButton edge="end">
                        <MoreVertIcon />
                      </IconButton>
                    }
                    divider
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: stringToColor(user.name) }}>
                        {getInitials(user.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.name}
                      secondary={
                        <>
                          {user.email} • Joined {user.date}
                        </>
                      }
                    />
                    <Chip 
                      label={user.plan} 
                      size="small"
                      color={
                        user.plan === 'Pro' ? 'primary' : 
                        user.plan === 'Enterprise' ? 'secondary' : 'default'
                      }
                      sx={{ mr: 2 }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button onClick={() => navigate('/admin/users')}>View All Users</Button>
              </Box>
            </>
          )}
          
          {/* Recent Transactions Tab */}
          {tabValue === 1 && (
            <>
              <List disablePadding>
                {recentTransactions.map((transaction) => (
                  <ListItem 
                    key={transaction.id}
                    secondaryAction={
                      <IconButton edge="end">
                        <MoreVertIcon />
                      </IconButton>
                    }
                    divider
                  >
                    <ListItemIcon>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={transaction.amount}
                      secondary={
                        <>
                          {transaction.plan} • {transaction.date}
                        </>
                      }
                    />
                    <Chip 
                      label={transaction.status} 
                      size="small"
                      color="success"
                      sx={{ mr: 2 }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button onClick={() => navigate('/admin/transactions')}>View All Transactions</Button>
              </Box>
            </>
          )}
          
          {/* Support Tickets Tab */}
          {tabValue === 2 && (
            <>
              <List disablePadding>
                {recentTickets.map((ticket) => (
                  <ListItem 
                    key={ticket.id}
                    secondaryAction={
                      <IconButton edge="end">
                        <MoreVertIcon />
                      </IconButton>
                    }
                    divider
                  >
                    <ListItemIcon>
                      {ticket.priority === 'High' ? (
                        <WarningIcon color="error" />
                      ) : (
                        <SupportAgentIcon />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={ticket.subject}
                      secondary={
                        <>
                          {ticket.user} • {ticket.date}
                        </>
                      }
                    />
                    <Chip 
                      label={ticket.status} 
                      size="small"
                      color={ticket.status === 'Open' ? 'error' : 'warning'}
                      sx={{ mr: 1 }}
                    />
                    <Chip 
                      label={ticket.priority} 
                      size="small"
                      color={
                        ticket.priority === 'High' ? 'error' : 
                        ticket.priority === 'Medium' ? 'warning' : 'info'
                      }
                      sx={{ mr: 2 }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button onClick={() => navigate('/admin/tickets')}>View All Tickets</Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
      
      {/* Quick Access Cards */}
      <Typography variant="h6" gutterBottom>Quick Access</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/users')}>
            <CardContent>
              <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/plans')}>
            <CardContent>
              <AttachMoneyIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Plans</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/tickets')}>
            <CardContent>
              <SupportAgentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Support</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/transactions')}>
            <CardContent>
              <ReceiptIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Billing</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/blog')}>
            <CardContent>
              <ArticleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Blog</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/marketing')}>
            <CardContent>
              <CampaignIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="subtitle1">Marketing</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminDashboard;
